import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone, DustExZoneType } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone, ExZoneType } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone, WildcardZoneType } from "@domain/project/floorplan/zones/wildcard-zone";
import { TableColumn } from "@pdf/helpers/default-table/table-column";
import { Headlines, ImagePaths, TableProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { FloorplanInformationTable } from "../floorplan-information-table";
import { ZoneRow } from "./zone-row";

export class ZonesRowHeaders {
  public readonly NUMBER = $localize`:@@pdfExport.zonesTable.header.number:Nr.`;
  public readonly DISPLAY_NAME = $localize`:@@pdfExport.zonesTable.header.displayName:Anzeigename`;
  public readonly TYPE = $localize`:@@pdfExport.zonesTable.header.type:Typ`;
  public readonly NOTES = $localize`:@@pdfExport.zonesTable.header.notes:Notizen`;
}

export class ZonesTable extends FloorplanInformationTable {
  readonly headlines = new Headlines();
  readonly rowHeaders = new ZonesRowHeaders();
  override readonly headline = this.headlines.ZONES;
  override readonly columns: TableColumn[] = [];
  override readonly rows: ZoneRow[];

  constructor(
    pdf: jsPDF,
    exZones: ExZone[],
    dustExZones: DustExZone[],
    dangerAreas: DangerArea[],
    wildcardZones: WildcardZone[],
    isolationRooms: IsolationRoom[],
    itRooms: ItRoom[],
    operatingTheatres: OperatingTheatre[],
    procedureRooms: ProcedureRoom[],
    standardRooms: StandardRoom[],
    private dangerAreaTypeText: string,
  ) {
    super(ImagePaths.ZONE);
    this.columns = [
      new TableColumn(this.rowHeaders.NUMBER, 48, 120),
      new TableColumn(this.rowHeaders.DISPLAY_NAME, 168, 156),
      new TableColumn(this.rowHeaders.TYPE, 348, 180),
      new TableColumn(this.rowHeaders.NOTES, 552, 888),
    ];
    this.rows = this.collectZoneRows(
      pdf,
      exZones,
      dustExZones,
      dangerAreas,
      wildcardZones,
      isolationRooms,
      itRooms,
      operatingTheatres,
      procedureRooms,
      standardRooms,
    );
    this.height = this.calculateHeight(this.rows);
  }

  private getExZoneTypeText(type: ExZoneType) {
    switch (type) {
      case ExZoneType.ZONE_0:
        return $localize`:@@global.exZone:Ex-Zone` + " 0";
      case ExZoneType.ZONE_1:
        return $localize`:@@global.exZone:Ex-Zone` + " 1";
      case ExZoneType.ZONE_2:
        return $localize`:@@global.exZone:Ex-Zone` + " 2";
    }
  }

  private getDustExZoneTypeText(type: DustExZoneType) {
    switch (type) {
      case DustExZoneType.ZONE_20:
        return $localize`:@@global.dustExZone:Staub-Ex-Zone` + " 20";
      case DustExZoneType.ZONE_21:
        return $localize`:@@global.dustExZone:Staub-Ex-Zone` + " 21";
      case DustExZoneType.ZONE_22:
        return $localize`:@@global.dustExZone:Staub-Ex-Zone` + " 22";
    }
  }

  private getWildcardZoneTypeText(type: WildcardZoneType) {
    switch (type) {
      case WildcardZoneType.ZONE_0:
        return $localize`:@@global.wildcardZone:Variable Zone` + " 0";
      case WildcardZoneType.ZONE_1:
        return $localize`:@@global.wildcardZone:Variable Zone` + " 1";
      case WildcardZoneType.ZONE_2:
        return $localize`:@@global.wildcardZone:Variable Zone` + " 2";
    }
  }

  private collectZoneRows(
    pdf: jsPDF,
    exZones: ExZone[],
    dustExZones: DustExZone[],
    dangerAreas: DangerArea[],
    wildcardZones: WildcardZone[],
    isolationRooms: IsolationRoom[],
    itRooms: ItRoom[],
    operatingTheatres: OperatingTheatre[],
    procedureRooms: ProcedureRoom[],
    standardRooms: StandardRoom[],
  ) {
    const exZoneRows = exZones.map(
      (zone) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(zone.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(zone.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getExZoneTypeText(zone.exZoneType)),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(zone.notes), this.rowHeaders.NOTES),
        ),
    );
    const dustExZoneRows = dustExZones.map(
      (zone) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(zone.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(zone.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getDustExZoneTypeText(zone.dustExZoneType)),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(zone.notes), this.rowHeaders.NOTES),
        ),
    );
    const dangerAreaRows = dangerAreas.map(
      (area) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getDangerAreaTypeText()),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(area.notes), this.rowHeaders.NOTES),
        ),
    );
    const wildcardZoneRows = wildcardZones.map(
      (zone) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(zone.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(zone.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getWildcardZoneTypeText(zone.type)),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(zone.notes), this.rowHeaders.NOTES),
        ),
    );
    const isolationRoomRows = isolationRooms.map(
      (area) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getIsolationRoomTypeText()),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(area.notes), this.rowHeaders.NOTES),
        ),
    );
    const itRoomRows = itRooms.map(
      (area) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getItRoomTypeText()),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(area.notes), this.rowHeaders.NOTES),
        ),
    );
    const operatingTheatreRows = operatingTheatres.map(
      (area) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getOperatingTheatreTypeText()),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(area.notes), this.rowHeaders.NOTES),
        ),
    );
    const procedureRoomRows = procedureRooms.map(
      (area) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getProcedureRoomTypeText()),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(area.notes), this.rowHeaders.NOTES),
        ),
    );
    const standardRoomRows = standardRooms.map(
      (area) =>
        new ZoneRow(
          pdf,
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.positionId),
            this.rowHeaders.NUMBER,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(area.displayName),
            this.rowHeaders.DISPLAY_NAME,
          ),
          this.textToColumnWidth(
            pdf,
            TableProperties.TEXT_FONT_SIZE,
            this.preProcessText(this.getStandardRoomTypeText()),
            this.rowHeaders.TYPE,
          ),
          this.textToColumnWidth(pdf, TableProperties.TEXT_FONT_SIZE, this.preProcessText(area.notes), this.rowHeaders.NOTES),
        ),
    );
    return exZoneRows
      .concat(dustExZoneRows)
      .concat(dangerAreaRows)
      .concat(wildcardZoneRows)
      .concat(isolationRoomRows)
      .concat(itRoomRows)
      .concat(operatingTheatreRows)
      .concat(procedureRoomRows)
      .concat(standardRoomRows)
      .sort((a, b) => a.number.join("").localeCompare(b.number.join("")));
  }

  private getDangerAreaTypeText() {
    return this.dangerAreaTypeText;
  }

  private getIsolationRoomTypeText() {
    return $localize`:@@global.isolationRoom:Isolation room`;
  }

  private getItRoomTypeText() {
    return $localize`:@@global.itRoom:IT room`;
  }

  private getOperatingTheatreTypeText() {
    return $localize`:@@global.operatingTheatre:Operating theatre`;
  }

  private getProcedureRoomTypeText() {
    return $localize`:@@global.procedureRoom:Procedure room`;
  }

  private getStandardRoomTypeText() {
    return $localize`:@@global.standardRoom:Standard room`;
  }
}
